import axios from 'axios'
import { config } from '@/config/environment'
import { BILLING_BLOCKED_EVENT, billingBlockMessage } from '@/utils/billing-block'

// Create axios instance with base URL from environment
// Uses proxy in development, direct URL in production
export const api = axios.create({
  baseURL: config.apiBaseUrl
})

// Reserved first-path-segments that look like org slugs to a naïve parser
// but are actually app-level routes. Sending these as X-Organization-Slug
// triggers the wrong code path on the api: in the best case the api rejects
// it as an invalid slug; in the worst case it briefly looked like a real org
// and routed mutations to the wrong tenant before the backend access check
// landed (api branch fix-org-slug-access-validation).
//
// Keep this in sync with App.tsx's non-:orgSlug routes and with the api's
// _is_valid_org_slug invalid_slugs set in api/app/dependencies/auth_headers.py.
const NON_ORG_FIRST_SEGMENTS = new Set([
  'oauth',          // /oauth/callback
  'login', 'logout', 'authenticate',
  'auth', 'register', 'signup',
  'api', 'docs', 'health', 'static', 'assets', 'public',
  'admin', 'dashboard', 'settings', 'profile', 'account',
  'get-started', 'get_started',
])

// Helper to extract org slug from current URL.
//
// Returns null when the first path segment does not look like an org slug
// (matches a known app-level route, fails length sanity check, or contains
// invalid characters). Callers fall back to localStorage.activeOrganization.
const getOrgSlugFromUrl = (): string | null => {
  const pathname = window.location.pathname
  const parts = pathname.substring(1).split('/')
  const candidate = parts[0]
  if (!candidate) return null
  if (NON_ORG_FIRST_SEGMENTS.has(candidate.toLowerCase())) return null
  // Org slugs are 3-50 chars, [a-zA-Z0-9_-]. Mirrors api _is_valid_org_slug.
  if (!/^[a-zA-Z0-9_-]{3,50}$/.test(candidate)) return null
  return candidate
}

// Add auth token and organization to requests (skip org header for auth endpoints)
api.interceptors.request.use(
  (config) => {
    const authToken = localStorage.getItem('authToken')
    const activeOrganization = localStorage.getItem('activeOrganization')
    
    config.headers = config.headers || {}
    
    // Add auth token
    if (authToken) {
      // Add Bearer prefix if not already present
      if (!authToken.startsWith('Bearer ')) {
        config.headers.Authorization = `Bearer ${authToken}`
      } else {
        config.headers.Authorization = authToken
      }
    }
    
    // Add organization slug header for non-auth endpoints only
    const authEndpoints = ['/user', '/login', '/logout', '/auth/', '/oauth/callback', '/get_started', '/authenticate', '/new_confirmation', '/invitation', '/marketplace', '/organizations', '/user/roles']
    // Use more specific matching to avoid /users being caught by /user
    const isAuthEndpoint = authEndpoints.some(endpoint => {
      if (endpoint === '/user') {
        // Exact match for /user to avoid catching /users
        return config.url === '/user'
      }
      if (endpoint === '/oauth/callback') {
        // Exact match for OAuth callback endpoint only
        return config.url?.includes('/oauth/callback')
      }
      if (endpoint === '/organizations') {
        // Exact match for /organizations list, not /organizations/{id} or sub-resources
        return config.url === '/organizations'
      }
      return config.url?.includes(endpoint)
    })
    
    // Also skip org slug header if we're on setup pages
    const isSetupPage = window.location.pathname.startsWith('/get-started/')
    
    // Special handling for OAuth popup windows - use organization from localStorage only
    const isOauthPopup = window.opener !== null && window.location.pathname.includes('oauth')
    
    if (!isAuthEndpoint && !isSetupPage) {
      let orgSlug
      
      if (isOauthPopup) {
        // In OAuth popups, rely only on localStorage for organization context
        // to avoid URL parsing issues in popup windows
        orgSlug = activeOrganization
        console.log('OAuth popup detected - using org from localStorage:', orgSlug)
      } else {
        // In main window, first try to get from URL, then fallback to localStorage
        orgSlug = getOrgSlugFromUrl() || activeOrganization
      }
      
      if (orgSlug) {
        config.headers['X-Organization-Slug'] = orgSlug
        
        // Only add organization ID for OAuth endpoints that specifically need it
        if (config.url && config.url.includes('/oauth/')) {
          try {
            const organizationsJson = localStorage.getItem('organizations')
            if (organizationsJson) {
              const organizations = JSON.parse(organizationsJson)
              const currentOrg = organizations.find((org: any) => org.slug === orgSlug)
              if (currentOrg && currentOrg.id) {
                config.headers['X-Organization-Id'] = currentOrg.id.toString()
              }
            }
          } catch (e) {
            console.warn('Failed to parse organizations from localStorage:', e)
          }
        }
        
        const headers: { slug: string; id?: string } = { slug: orgSlug }
        if (config.headers['X-Organization-Id']) {
          headers.id = config.headers['X-Organization-Id']
        }
        // Headers added for organization context
      } else {
        // No organization context found
      }
    } else {
      // Skipping org headers for auth endpoints
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Handle response errors
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // Billing gate: the org is over its band / trial. Tag the error and announce it
    // ONCE so the guard can render the upgrade screen. Every component that happens
    // to be mounted hits a gated endpoint, so without this a blocked org produces a
    // console full of identical 403s.
    const blockMessage = billingBlockMessage(error)
    if (blockMessage) {
      error.isBillingBlock = true
      window.dispatchEvent(
        new CustomEvent(BILLING_BLOCKED_EVENT, { detail: { message: blockMessage } }),
      )
      return Promise.reject(error)
    }

    if (error.response?.status === 401) {
      const url = error.config?.url || ''
      console.log('401 Unauthorized error received for URL:', url)

      // Skip auth error handling for endpoints that don't require authentication
      // or where 401 is an expected response (e.g., wrong password on login)
      const skipAuthErrorUrls = ['/authenticate', '/analytics/track', '/auth/']
      const shouldSkip = skipAuthErrorUrls.some(skipUrl => url.includes(skipUrl))

      if (shouldSkip) {
        console.log('Skipping auth error handling for URL:', url)
        return Promise.reject(error)
      }

      // CRITICAL: Don't auto-refresh in popup windows to preserve OAuth flow
      const isPopup = window.opener !== null
      if (isPopup) {
        console.log('OAuth popup detected - skipping automatic session handling')
        return Promise.reject(error)
      }

      // In main window, emit custom event for components to handle
      window.dispatchEvent(new CustomEvent('auth-error', {
        detail: {
          status: 401,
          url: url
        }
      }))

      console.log('Authentication error detected, manual intervention required')
    }
    return Promise.reject(error)
  }
)